import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { PageHeader } from "@/components/site/PageHeader";
import { SiteFooter } from "@/components/site/FinalCTA";
import { useAuth } from "@/context/AuthContext";
import { treesApi } from "@/lib/treesApi";
import { layoutTree, LAYOUT_CONSTANTS } from "@/lib/treeLayout";
import PersonDialog from "@/components/builder/PersonDialog";
import LinkPickerDialog from "@/components/builder/LinkPickerDialog";
import TreeCanvas from "@/components/builder/TreeCanvas";
import AuthDialog from "@/components/site/AuthDialog";
import { BUILDER } from "@/constants/testIds";
import { formatApiError } from "@/lib/api";

function errMsg(e) {
  return formatApiError(e?.response?.data?.detail) || e?.message || "Something went wrong.";
}

export default function MyTreePage() {
  const { user, ready } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);

  const [trees, setTrees] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [tree, setTree] = useState(null);
  const [people, setPeople] = useState([]);
  const [rels, setRels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [personDialog, setPersonDialog] = useState({ open: false, mode: "create", person: null });
  const [linkPicker, setLinkPicker] = useState({ open: false, kind: null });

  // ------- data loading -------
  const refreshTrees = useCallback(async () => {
    const data = await treesApi.list();
    setTrees(data.trees);
    return data.trees;
  }, []);

  const loadTree = useCallback(async (id) => {
    setLoading(true);
    try {
      const data = await treesApi.get(id);
      setTree(data.tree);
      setPeople(data.people);
      setRels(data.relationships);
      // Preserve selection if it still exists
      if (data.people.length && !data.people.find((p) => p.id === selectedId)) {
        setSelectedId(null);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    if (!ready || !user) return;
    (async () => {
      try {
        const list = await refreshTrees();
        if (list.length) setActiveId((prev) => prev || list[0].id);
      } catch (e) {
        toast.error(errMsg(e));
      }
    })();
  }, [ready, user, refreshTrees]);

  useEffect(() => {
    if (activeId) loadTree(activeId);
  }, [activeId, loadTree]);

  // ------- derived layout -------
  const layout = useMemo(() => layoutTree(people, rels), [people, rels]);
  const selectedPerson = useMemo(
    () => people.find((p) => p.id === selectedId) || null,
    [people, selectedId],
  );

  // ------- handlers -------
  async function handleCreateTree(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const t = await treesApi.create(newName.trim());
      setNewName("");
      const list = await refreshTrees();
      setActiveId(t.id);
      toast.success("New tree created.");
      void list;
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setCreating(false);
    }
  }

  async function handleRenameTree() {
    if (!tree) return;
    const next = window.prompt("Rename this tree:", tree.name);
    if (!next || !next.trim() || next.trim() === tree.name) return;
    try {
      await treesApi.rename(tree.id, next.trim());
      await refreshTrees();
      await loadTree(tree.id);
      toast.success("Tree renamed.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  }

  async function handleDeleteTree() {
    if (!tree) return;
    if (!window.confirm(`Delete tree "${tree.name}" and everyone in it? This can't be undone.`)) return;
    try {
      await treesApi.remove(tree.id);
      const list = await refreshTrees();
      setActiveId(list.length ? list[0].id : null);
      if (!list.length) {
        setTree(null); setPeople([]); setRels([]);
      }
      toast.success("Tree deleted.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  }

  async function submitPerson(values) {
    try {
      if (personDialog.mode === "edit" && personDialog.person) {
        const updated = await treesApi.editPerson(tree.id, personDialog.person.id, values);
        setPeople((cur) => cur.map((p) => (p.id === updated.id ? updated : p)));
        toast.success("Saved.");
      } else {
        const created = await treesApi.addPerson(tree.id, values);
        setPeople((cur) => [...cur, created]);
        // If this new person was added via a relation intent, apply it
        if (personDialog.link) {
          await applyLink(personDialog.link, created.id);
        } else {
          setSelectedId(created.id);
        }
        toast.success("Added.");
      }
      setPersonDialog({ open: false, mode: "create", person: null });
      // Reload to make sure tree.updated_at / is_you swaps are consistent
      await loadTree(tree.id);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }

  async function deletePerson() {
    if (!personDialog.person) return;
    if (!window.confirm(`Remove ${personDialog.person.name} from the tree?`)) return;
    try {
      await treesApi.deletePerson(tree.id, personDialog.person.id);
      setPersonDialog({ open: false, mode: "create", person: null });
      if (selectedId === personDialog.person.id) setSelectedId(null);
      await loadTree(tree.id);
      toast.success("Removed.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  }

  async function applyLink(link, otherId) {
    // link = { kind: "child" | "parent" | "spouse", anchorId }
    try {
      const anchor = link.anchorId;
      if (link.kind === "child") {
        await treesApi.addRelationship(tree.id, { type: "parent", from_id: anchor, to_id: otherId });
      } else if (link.kind === "parent") {
        await treesApi.addRelationship(tree.id, { type: "parent", from_id: otherId, to_id: anchor });
      } else if (link.kind === "spouse") {
        await treesApi.addRelationship(tree.id, { type: "spouse", from_id: anchor, to_id: otherId });
      }
      await loadTree(tree.id);
      setSelectedId(otherId);
      toast.success("Linked.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  }

  function openAddPersonForLink(kind) {
    if (!selectedPerson) return;
    setLinkPicker({ open: true, kind, anchorId: selectedPerson.id });
  }

  async function chooseLinkExisting(otherId) {
    if (!linkPicker.anchorId) return;
    await applyLink({ kind: linkPicker.kind, anchorId: linkPicker.anchorId }, otherId);
    setLinkPicker({ open: false, kind: null });
  }

  function chooseLinkNew() {
    setLinkPicker({ open: false, kind: null });
    setPersonDialog({
      open: true,
      mode: "create",
      person: null,
      link: { kind: linkPicker.kind, anchorId: linkPicker.anchorId },
    });
  }

  async function removeRelationship(relId) {
    try {
      await treesApi.deleteRelationship(tree.id, relId);
      await loadTree(tree.id);
      toast.success("Link removed.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  }

  // ------- render -------
  if (!ready) {
    return <div className="min-h-screen bg-ivory" />;
  }

  if (!user) {
    return (
      <div data-testid={BUILDER.page} className="min-h-screen bg-ivory text-ink">
        <PageHeader
          eyebrow="Family Tree Builder"
          title="Your tree,"
          italic="rooted"
          tail="in ivory and time."
          subtitle="Sign in to begin your archive. Each name you add becomes part of a living family record — one that your grandchildren will still be able to open."
        />
        <div className="max-w-[900px] mx-auto px-6 md:px-10 py-20">
          <div data-testid={BUILDER.loginPrompt} className="rounded-2xl border border-ivory-300 bg-white/60 p-10 text-center">
            <p className="font-serif text-2xl text-ink">Sign in to build your family tree.</p>
            <p className="mt-2 text-ink-50">Every archive is private to you until you share it.</p>
            <button
              data-testid={BUILDER.loginPromptBtn}
              onClick={() => setAuthOpen(true)}
              className="mt-8 rounded-full bg-saffron text-ivory px-6 py-3 font-medium hover:bg-saffron-deep transition"
            >
              Sign in or register
            </button>
          </div>
        </div>
        <AuthDialog open={authOpen} onOpenChange={setAuthOpen} initialTab="register" />
        <SiteFooter />
      </div>
    );
  }

  const emptyState = !loading && tree && people.length === 0;
  const noTrees = !loading && trees.length === 0;

  return (
    <div data-testid={BUILDER.page} className="min-h-screen bg-ivory text-ink">
      <PageHeader
        eyebrow="Family Tree Builder"
        title="Your tree,"
        italic="rooted"
        tail="in ivory and time."
        subtitle="Add ancestors, siblings, spouses and children. Connect the branches. Everything you save here belongs to your archive — private, permanent, and only ever yours."
      />

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-10 pb-20">
        {/* Toolbar */}
        <div className="flex flex-wrap gap-3 items-center border-b border-ivory-300 pb-6">
          {trees.length > 0 && (
            <>
              <label className="text-xs uppercase tracking-[0.2em] text-ink-50">Tree</label>
              <select
                data-testid={BUILDER.treeSelector}
                value={activeId || ""}
                onChange={(e) => setActiveId(e.target.value)}
                className="h-10 rounded-full border border-ivory-300 bg-ivory pl-4 pr-8 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-tulsi"
              >
                {trees.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} · {t.people_count} people</option>
                ))}
              </select>
              <button
                data-testid={BUILDER.renameBtn}
                onClick={handleRenameTree}
                className="text-xs uppercase tracking-[0.2em] text-ink-50 hover:text-ink px-2"
              >
                Rename
              </button>
              <button
                data-testid={BUILDER.deleteTreeBtn}
                onClick={handleDeleteTree}
                className="text-xs uppercase tracking-[0.2em] text-rust hover:text-rust/70 px-2"
              >
                Delete tree
              </button>
              <div className="w-px h-6 bg-ivory-300 mx-1" />
            </>
          )}

          <form onSubmit={handleCreateTree} className="flex items-center gap-2">
            <input
              data-testid={BUILDER.newTreeInput}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Name a new tree (e.g. Menon family)"
              className="h-10 rounded-full border border-ivory-300 bg-ivory px-4 text-sm placeholder:text-ink-50/60 focus:outline-none focus:ring-2 focus:ring-tulsi w-[280px]"
            />
            <button
              data-testid={BUILDER.newTreeSubmit}
              disabled={creating || !newName.trim()}
              className="h-10 rounded-full bg-tulsi text-ivory px-4 text-sm font-medium tracking-wide hover:bg-tulsi-deep disabled:opacity-60 transition"
            >
              {creating ? "Creating…" : "New tree"}
            </button>
          </form>

          <div className="ml-auto flex items-center gap-2">
            {tree && (
              <button
                data-testid={BUILDER.toolbarAdd}
                onClick={() => setPersonDialog({ open: true, mode: "create", person: null })}
                className="h-10 rounded-full bg-saffron text-ivory px-5 text-sm font-medium hover:bg-saffron-deep transition"
              >
                + Add person
              </button>
            )}
          </div>
        </div>

        {/* Empty states */}
        {noTrees && (
          <div className="mt-16 rounded-2xl border border-ivory-300 bg-white/60 p-14 text-center">
            <p className="font-serif text-3xl text-ink">Begin the archive.</p>
            <p className="mt-2 text-ink-50 max-w-md mx-auto">
              Name your first tree. You could start with your surname, your grandfather's name, or the village your family came from.
            </p>
          </div>
        )}

        {tree && (
          <div className="mt-8 grid grid-cols-12 gap-6">
            {/* Canvas */}
            <div className="col-span-12 lg:col-span-8">
              <div className="rounded-2xl border border-ivory-300 bg-white/70 overflow-hidden">
                <div className="px-5 py-3 border-b border-ivory-300 flex items-center justify-between">
                  <div className="font-serif text-lg text-ink truncate">{tree.name}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-ink-50">
                    {people.length} {people.length === 1 ? "person" : "people"} · {rels.length} link{rels.length === 1 ? "" : "s"}
                  </div>
                </div>
                {emptyState ? (
                  <div className="p-12 text-center">
                    <p className="font-serif text-2xl text-ink">Add your first person.</p>
                    <p className="mt-2 text-ink-50">Start with yourself, or the eldest relative you know.</p>
                    <button
                      data-testid={BUILDER.addFirstPerson}
                      onClick={() => setPersonDialog({ open: true, mode: "create", person: null })}
                      className="mt-6 rounded-full bg-saffron text-ivory px-5 py-2.5 text-sm font-medium hover:bg-saffron-deep transition"
                    >
                      + Add first person
                    </button>
                  </div>
                ) : (
                  <TreeCanvas
                    people={people}
                    rels={rels}
                    layout={layout}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    layoutConstants={LAYOUT_CONSTANTS}
                  />
                )}
              </div>
              <p className="mt-3 text-xs text-ink-50">
                Tap a person to see details. Use the panel on the right to add parents, spouses or children.
              </p>
            </div>

            {/* Side panel */}
            <div className="col-span-12 lg:col-span-4">
              <AnimatePresence mode="wait">
                {selectedPerson ? (
                  <motion.div
                    key={selectedPerson.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="rounded-2xl border border-ivory-300 bg-ivory p-6 sticky top-32"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-[999px_999px_16px_16px] overflow-hidden border border-ivory-300 bg-ivory-200 flex items-center justify-center shrink-0">
                        {selectedPerson.portrait_url ? (
                          <img src={selectedPerson.portrait_url} alt={selectedPerson.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="font-serif text-tulsi text-2xl">
                            {selectedPerson.name.trim().charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        {selectedPerson.is_you && (
                          <div className="text-[10px] uppercase tracking-[0.24em] text-saffron">You</div>
                        )}
                        <div className="font-serif text-2xl text-ink leading-tight truncate">{selectedPerson.name}</div>
                        <div className="text-xs text-ink-50 mt-0.5">
                          {[selectedPerson.birth_year, selectedPerson.death_year]
                            .filter(Boolean)
                            .join(" — ") || "Dates unknown"}
                        </div>
                      </div>
                    </div>

                    <div className="mt-5 space-y-3 text-sm">
                      {selectedPerson.birth_place && (
                        <Row label="Home" value={selectedPerson.birth_place} />
                      )}
                      {selectedPerson.gender && (
                        <Row label="Gender" value={cap(selectedPerson.gender)} />
                      )}
                      {selectedPerson.notes && (
                        <Row label="Story" value={selectedPerson.notes} />
                      )}
                    </div>

                    <div className="mt-5 grid grid-cols-3 gap-2">
                      <IconBtn testId={BUILDER.addParentBtn} label="Parent" onClick={() => openAddPersonForLink("parent")} />
                      <IconBtn testId={BUILDER.addSpouseBtn} label="Spouse" onClick={() => openAddPersonForLink("spouse")} />
                      <IconBtn testId={BUILDER.addChildBtn} label="Child" onClick={() => openAddPersonForLink("child")} />
                    </div>

                    <div className="mt-5 flex gap-2">
                      <button
                        onClick={() => setPersonDialog({ open: true, mode: "edit", person: selectedPerson })}
                        className="flex-1 h-10 rounded-full bg-tulsi text-ivory text-sm font-medium hover:bg-tulsi-deep transition"
                      >
                        Edit details
                      </button>
                    </div>

                    <RelationsList
                      person={selectedPerson}
                      people={people}
                      rels={rels}
                      onSelect={setSelectedId}
                      onRemove={removeRelationship}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="empty-side"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="rounded-2xl border border-ivory-300 bg-white/60 p-6 sticky top-32"
                  >
                    <p className="font-serif text-lg text-ink">Select someone to begin.</p>
                    <p className="mt-2 text-sm text-ink-50">Or add a new person to start branching outward.</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>

      <PersonDialog
        open={personDialog.open}
        mode={personDialog.mode}
        initial={personDialog.person}
        linkContext={personDialog.link && selectedPerson
          ? { kind: personDialog.link.kind, anchorName: selectedPerson.name }
          : null}
        onClose={() => setPersonDialog({ open: false, mode: "create", person: null })}
        onSubmit={submitPerson}
        onDelete={personDialog.mode === "edit" ? deletePerson : null}
      />

      <LinkPickerDialog
        open={linkPicker.open}
        kind={linkPicker.kind}
        anchor={selectedPerson}
        candidates={people.filter((p) => p.id !== selectedPerson?.id)}
        onClose={() => setLinkPicker({ open: false, kind: null })}
        onPickExisting={chooseLinkExisting}
        onPickNew={chooseLinkNew}
      />

      <SiteFooter />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-ink-50 min-w-[64px] text-xs uppercase tracking-[0.16em] pt-1">{label}</span>
      <span className="text-ink font-light leading-relaxed">{value}</span>
    </div>
  );
}

function IconBtn({ label, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="h-10 rounded-xl border border-ivory-300 bg-white/70 text-xs uppercase tracking-[0.14em] text-ink hover:border-saffron hover:text-saffron transition"
    >
      + {label}
    </button>
  );
}

function cap(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function RelationsList({ person, people, rels, onSelect, onRemove }) {
  const byId = Object.fromEntries(people.map((p) => [p.id, p]));
  const parents = rels
    .filter((r) => r.type === "parent" && r.to_id === person.id)
    .map((r) => ({ rel: r, other: byId[r.from_id] }))
    .filter((x) => x.other);
  const children = rels
    .filter((r) => r.type === "parent" && r.from_id === person.id)
    .map((r) => ({ rel: r, other: byId[r.to_id] }))
    .filter((x) => x.other);
  const spouses = rels
    .filter((r) => r.type === "spouse" && (r.from_id === person.id || r.to_id === person.id))
    .map((r) => ({ rel: r, other: byId[r.from_id === person.id ? r.to_id : r.from_id] }))
    .filter((x) => x.other);

  const Section = ({ title, items }) => {
    if (!items.length) return null;
    return (
      <div className="mt-4">
        <div className="text-xs uppercase tracking-[0.2em] text-ink-50">{title}</div>
        <ul className="mt-2 space-y-1">
          {items.map(({ rel, other }) => (
            <li key={rel.id} className="group flex items-center justify-between text-sm">
              <button
                onClick={() => onSelect(other.id)}
                className="text-ink hover:text-saffron transition truncate"
              >
                {other.name}
              </button>
              <button
                onClick={() => onRemove(rel.id)}
                className="opacity-0 group-hover:opacity-100 text-xs text-rust hover:text-rust/70 transition"
                title="Unlink"
              >
                unlink
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="mt-6 border-t border-ivory-300 pt-4">
      <Section title="Parents" items={parents} />
      <Section title="Spouse" items={spouses} />
      <Section title="Children" items={children} />
      {!parents.length && !spouses.length && !children.length && (
        <p className="text-xs text-ink-50">No connections yet. Add a parent, spouse or child above.</p>
      )}
    </div>
  );
}
